import { Link } from 'react-router-dom';
import { fetchInsightsMargin } from '../../adminApi';
import { Panel } from '../../ui/Panel';
import { ReportView } from '../ReportView';
import { useReport } from '../useReport';
import { orderKindLabel } from '../../../core/rules/margin';
import { euro, money } from '../../../core/format/currency';
import { plural } from '../../../core/format/text';
import { useCan } from '../../useCan';
import type { PeriodState } from '../../sales/usePeriodParams';
import type { MarginReport } from '../../../core/types/margin';
import type { RequestOptions } from '../../../core/types/backend';
const whole = (cents: number) => euro(Math.round(cents / 100));
type MarginFetch = (from: string, to: string, opts?: RequestOptions) => Promise<MarginReport | null>;
const noMargin: MarginFetch = () => Promise.resolve(null);

/**
 * Inzicht › Marge: wat er overbleef in deze periode, per stroom, en waar de kosten zitten.
 * Bijdrage aan vaste lasten, geen nettowinst. Alleen met `finance.margin`.
 */
export function MarginTab({
  period
}: {
  period: PeriodState;
}) {
  // Ook hier zelf controleren: zonder bevoegdheid geen aanvraag (de server zou 403 geven).
  const {
    canAll
  } = useCan();
  const allowed = canAll('finance.margin');
  const fetcher: MarginFetch = allowed ? fetchInsightsMargin : noMargin;
  const report = useReport(fetcher, period.from, period.to);
  if (!allowed) return null;
  return <ReportView report={report} what="De margecijfers" data-id="element-2086" data-mp="2mgdj77ud7h6i">
      {m => {
      const pct = m.revenueNetCents > 0 ? Math.round(m.leftCents / m.revenueNetCents * 100) : null;
      return <>
            <section aria-labelledby="marge-kop" className="rounded-lg border border-line bg-white p-5 sm:p-6" data-id="element-2087" data-mp="2dedzu8vvo10e">
              <h2 id="marge-kop" className="text-[13px] text-muted" data-id="element-2088" data-mp="28fau0m82pqrd">
                Wat er overbleef{m.estimated && ' · geschat'}
              </h2>
              <p className="mt-1 font-display text-[2.4rem] font-light leading-none tabular-nums text-ink" data-id="element-2089" data-mp="x0xaftkcrxh5">{whole(m.leftCents)}</p>
              <p className="mt-2 text-[13px] text-muted" data-id="element-2090" data-mp="yht7a75b0r4o">
                {pct !== null && `${pct}% van `}
                {whole(m.revenueNetCents)} omzet zonder btw · vóór vaste lasten en je eigen tijd
              </p>
            </section>

            <Panel title="Per stroom" description="Gesorteerd op wat het opleverde." flush data-id="element-2091" data-mp="ph4qj4oj0co3">
              <div className="overflow-x-auto" data-id="element-2092" data-mp="th6lkuzra7ue">
                <table className="w-full min-w-[40rem] text-[14px]" data-id="element-2093" data-mp="fr35klrgzade">
                  <caption className="sr-only" data-id="element-2094" data-mp="fps1sly0y2ma">Wat er overbleef per stroom</caption>
                  <thead className="text-[12px] text-muted" data-id="element-2095" data-mp="kjml3o9pwfku">
                    <tr className="border-b border-line" data-id="element-2096" data-mp="5uglhz6ywncf">
                      <th scope="col" className="px-4 py-2 text-left font-normal sm:px-5" data-id="element-2097" data-mp="asi30foqwels">Stroom</th>
                      <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-2098" data-mp="3sy4v3vj7y573">Omzet</th>
                      <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-2099" data-mp="3t0hco7l2edjm">Brutomarge</th>
                      <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-2100" data-mp="1tmwf2hsh2hy">Kosten / order</th>
                      <th scope="col" className="px-2 py-2 text-right font-normal" data-id="element-2101" data-mp="3ed93tdf8akat">Over / order</th>
                      <th scope="col" className="px-4 py-2 text-right font-normal sm:px-5" data-id="element-2102" data-mp="3nc4cqx9nq8ht">Totaal</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-line" data-id="element-2103" data-mp="3j9puofdtbcrl">
                    {m.streams.map(s => <tr key={s.kind} data-id="element-2104" data-mp="13l6c2wof7yd">
                        <th scope="row" className="px-4 py-3 text-left font-normal text-ink sm:px-5" data-id="element-2105" data-mp="3sb779ns0gwbj">
                          {orderKindLabel[s.kind]}
                          <span className="block text-[12px] text-muted" data-id="element-2106" data-mp="atea4hqkuis2">{plural(s.orders, 'order', 'orders')}</span>
                        </th>
                        <td className="px-2 py-3 text-right tabular-nums" data-id="element-2107" data-mp="jvdqr8xojkuh">{whole(s.revenueNetCents)}</td>
                        <td className="px-2 py-3 text-right tabular-nums text-muted" data-id="element-2108" data-mp="ewasq8g4aew0">{s.grossMarginPercent === null ? '—' : `${s.grossMarginPercent}%`}</td>
                        <td className="px-2 py-3 text-right tabular-nums text-muted" data-id="element-2109" data-mp="29bzti3z9uzrl">{money(s.costsPerOrderCents)}</td>
                        <td className={`px-2 py-3 text-right tabular-nums ${s.leftPerOrderCents < 1000 ? 'text-clay' : 'text-ink'}`} data-id="element-2110" data-mp="2e9hrx0fnxwlc">{money(s.leftPerOrderCents)}</td>
                        <td className="px-4 py-3 text-right tabular-nums text-ink sm:px-5" data-id="element-2111" data-mp="2n2mi33bbj99y">{whole(s.leftCents)}</td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </Panel>

            <Panel title="Waar de kosten zitten" data-id="element-2112" data-mp="1zebo8k41i9v6">
              <dl className="grid gap-x-8 gap-y-3 text-[14px] sm:grid-cols-2" data-id="element-2113" data-mp="1zdk4q7npw2l9">
                <Fact label="Gratis verzending kostte" value={whole(m.freeShippingCents)} hint="Pakket en verpakking die de klant niet betaalde" data-id="element-2114" data-mp="24doivr15kl8b" />
                <Fact label="Betaalkosten" value={whole(m.paymentCents)} data-id="element-2115" data-mp="3avl3svk1shr7" />
                <Fact label="Cadeaus" value={whole(m.giftCents)} hint="Staffel en abonnement; gratis samples tellen als € 0" data-id="element-2116" data-mp="3fr0g3j2eq5li" />
                <Fact label="Mislukte incasso's" value={`${m.failedDebits.count} · ${money(m.failedDebits.costCents)}`} hint="Kosten zonder omzet" data-id="element-2117" data-mp="3pymnrzfwh2oq" />
                <Fact label="Uitgespaard door slim bijvullen" value={plural(m.parcelsSaved, 'pakket', 'pakketten')} hint="Twee flesjes in één doos in plaats van twee keer verzenden" data-id="element-2118" data-mp="3g5xf1cbqi2x3" />
              </dl>
              {m.missingCost.length > 0 && <p className="mt-5 border-t border-line pt-4 text-[13px] text-clay" data-id="element-2119" data-mp="373xw2eh5sori">
                  Marge onvolledig: {plural(m.missingCost.length, 'product', 'producten')} zonder inkoopprijs ({m.missingCost.slice(0, 3).map(p => p.name).join(', ')}
                  {m.missingCost.length > 3 ? ', …' : ''}).{' '}
                  <Link to="/be/voorraad?tab=inkoop" className="text-ink underline underline-offset-4" data-id="element-2120" data-mp="vg7xusnk6518">
                    Inkoop boeken
                  </Link>
                </p>}
            </Panel>
          </>;
    }}
    </ReportView>;
}
function Fact({
  label,
  value,
  hint
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return <div className="flex items-baseline justify-between gap-4 border-b border-line pb-3" data-id="element-2121" data-mp="2ppmvzkyqbfxr">
      <dt className="min-w-0 text-muted" data-id="element-2122" data-mp="2byi08tyhf7sp">
        {label}
        {hint && <span className="block text-[12px]" data-id="element-2123" data-mp="2fxqstvhsczmo">{hint}</span>}
      </dt>
      <dd className="shrink-0 tabular-nums text-ink" data-id="element-2124" data-mp="33o3avz6qwcqz">{value}</dd>
    </div>;
}